import { t, useStoreLanguage, getDirection, getLanguage } from "../../../features/store-language/i18n.ts";
import { Link, Outlet, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { Tooltip } from "antd";
import { useState } from "react";
import { BookOpenText, Buildings, Heart, Receipt, UserCircle, UsersThree } from "@phosphor-icons/react";
import { AccountUIProvider } from "../../shared/account/AccountUI.tsx";
import PortalShell from "../../../Layout/PortalShell/PortalShell.tsx";
import ShopMedia from "../shared/ShopMedia.tsx";
import { agencyRoleLabel } from "../Auth/agencyRoleLabel.ts";
import { getAgencySession } from "../../../utils/agencySession.ts";
import type { ShopLayoutOutletContext } from "../../../Layout/ShopLayout.tsx";
import "./CustomerPanelLayout.css";
import PortalPageGuide from "../../../features/guides/PortalPageGuide.tsx";
import AgencyMembershipSelector from "../Auth/AgencyMembershipSelector.tsx";
import NotificationCenter from "../../../features/notifications/NotificationCenter.tsx";
import CustomerProfile from "../Profile/CustomerProfile.tsx";
import useMobileGrid from "../../shared/grid/useMobileGrid.ts";

function CustomerPanelLayout() {
  useStoreLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const mobile = useMobileGrid();
  const { companyInfo, logoutPending, handleLogout, mobileNavigation } = useOutletContext<ShopLayoutOutletContext>();
  const [profileOpen, setProfileOpen] = useState(false);
  const pathname = location.pathname.replace(/\/$/, "");
  const customerName = getAgencySession()?.displayName || t("نماینده گرامی");
  const items = [
    { key: "/account/orders", title: t("سفارش‌ها"), icon: <Receipt size={18} weight="duotone" /> },
    { key: "/account/favorites", title: t("علاقه‌مندی‌ها"), shortTitle: t("علاقه‌ها"), icon: <Heart size={18} weight="duotone" /> },
    { key: "/account/personnel", title: t("مدیریت پرسنل"), shortTitle: t("پرسنل"), icon: <UsersThree size={18} weight="duotone" />, disabled: getAgencySession()?.role !== "Manager" },
    { key: "account-profile-drawer", title: t("اطلاعات حساب"), icon: <UserCircle size={18} /> },
  ].filter(function (item) { return item.key !== "/account/personnel" || getAgencySession()?.role !== "CompanySalesAgent"; });
  const current = pathname === "/account/profile" ? "account-profile-drawer" : items.find(function (item) {
    return pathname === item.key || pathname.startsWith(`${item.key}/`);
  })?.key || "/account/orders";

  return (
    <PortalShell
      items={items}
      current={current}
      accountKey="account-profile-drawer"
      accountControl="drawer"
      accountOpen={profileOpen}
      onAccountOpen={function () {
        if (mobile) navigate("/account/profile");
        else setProfileOpen(true);
      }}
      home="/account/orders"
      onMobileBrandClick={function () { navigate("/products"); }}
      onNavigate={function (next) { navigate(next); }}
      title={t("پورتال من")}
      subtitle={customerName}
      headerIdentity={{
        title: customerName,
        subtitle: agencyRoleLabel(getAgencySession()?.role || ""),
      }}
      userName={customerName}
      preferencePrefix="portal-account"
      direction={getDirection()}
      language={getLanguage()}
      className="customer-panel"
      mobileNavigation={mobileNavigation}
      translate={t}
      loggingOut={logoutPending}
      onLogout={handleLogout}
      navigationMode="expandable"
      headerActions={(
        <>
          <AgencyMembershipSelector />
          <PortalPageGuide />
          <NotificationCenter />
        </>
      )}
      brand={
        <>
          <span className="portal-shell__tenant-logo">
            <ShopMedia
              key={companyInfo?.fileId}
              fileId={companyInfo?.fileId}
              alt=""
              className="portal-shell__tenant-logo-image"
              fallback={
                <Buildings
                  size={28}
                  aria-hidden="true"
                />
              }
            />
          </span>
          <span className="platform-admin__sr-only">
            {companyInfo?.title || t("پورتال من")}
          </span>
        </>
      }
      navigationStart={function (iconOnly) {
        return (
          <Tooltip
            title={iconOnly ? t("کاتالوگ") : undefined}
            trigger={["hover", "focus"]}
          >
            <Link
              to="/products"
              className="portal-shell__sidebar-action"
            >
              <BookOpenText size={22} weight="duotone" />
              <span className={iconOnly ? "platform-admin__sr-only" : undefined}>{t("کاتالوگ")}</span>
            </Link>
          </Tooltip>
        );
      }}
    >
      <AccountUIProvider>
        <main className="app-content">
          <div className="customer-panel-content">
            <Outlet />
          </div>
        </main>
        <CustomerProfile
          open={profileOpen}
          onClose={function () { setProfileOpen(false); }}
        />
      </AccountUIProvider>
    </PortalShell>
  );
}

export default CustomerPanelLayout;
